import type CompanyHotelBookingPaceRoomMappingTypeAvailabilities from '@app/model/pricing-automation/output/CompanyHotelBookingPaceRoomMappingTypeAvailabilities';
import type HotelRoomMappingType from '@app/model/market_state/HotelRoomMappingType';

export const FIXED_PICKUP_WINDOWS = [3, 7, 15, 30] as const;
export const PICKUP_WINDOWS = [3, 7, 15, 30, 0] as const;
export type PickupWindow = typeof PICKUP_WINDOWS[number];

type Availability = CompanyHotelBookingPaceRoomMappingTypeAvailabilities['availability'];

const AGO_FIELD: Record<typeof FIXED_PICKUP_WINDOWS[number], keyof Availability> = {
	3: 'availableRooms3dAgo',
	7: 'availableRooms7dAgo',
	15: 'availableRooms15dAgo',
	30: 'availableRooms30dAgo',
};

export type Pickup = {
	byWindow: Partial<Record<PickupWindow, number>>,
	booked: number | undefined,
	capacity: number | undefined,
	lastChange?: { changedAt: string; daysAgo: number; pickup: number | null; issues: string[] } | null,
};

const sumOf = (values: (number | null | undefined)[]): number | undefined => {
	const known = values.filter((value): value is number => typeof value === 'number' && Number.isFinite(value));
	return known.length === 0 || known.length !== values.length ? undefined : known.reduce((total, value) => total + value, 0);
};

// changedAt is a UTC LocalDateTime without a zone suffix. Date supports millisecond precision.
const changeInstant = (changedAt: string) => {
	const normalized = changedAt.replace(/\.(\d{3})\d+/, '.$1');
	return new Date(/(Z|[+-]\d\d:\d\d)$/.test(normalized) ? normalized : `${normalized}Z`);
};

// The calendar day of an instant in the time zone (undefined = the browser), as a UTC midnight.
const calendarDay = (instant: Date, timeZone?: string) => {
	const parts = new Intl.DateTimeFormat('en-US', { year: 'numeric', month: 'numeric', day: 'numeric', timeZone }).formatToParts(instant);
	const part = (type: Intl.DateTimeFormatPartTypes) => Number(parts.find(item => item.type === type)?.value);
	return Date.UTC(part('year'), part('month') - 1, part('day'));
};

// The Last-change age: calendar days between the change and today, in the time zone of the viewer.
export const lastChangeAge = (changedAt: string, now: Date, timeZone?: string): number =>
	Math.max(0, Math.round((calendarDay(now, timeZone) - calendarDay(changeInstant(changedAt), timeZone)) / 86_400_000));

// The date of the change in the time zone of the viewer, for example "11 Oct".
export const lastChangeDate = (changedAt: string, timeZone?: string): string => {
	const parts = new Intl.DateTimeFormat('en-US', { day: 'numeric', month: 'short', timeZone }).formatToParts(changeInstant(changedAt));
	return `${parts.find(part => part.type === 'day')?.value} ${parts.find(part => part.type === 'month')?.value}`;
};

// The Pickup of the hotel (roomMappingType undefined) or of one room type.
export const pickupFor = (
	availabilities: CompanyHotelBookingPaceRoomMappingTypeAvailabilities[] | undefined,
	roomMappingType?: HotelRoomMappingType,
): Pickup | undefined => {
	if (availabilities === undefined || availabilities.length === 0) return undefined;
	const entries = availabilities.filter(entry => !entry.isTotal && (roomMappingType === undefined || entry.roomMappingType === roomMappingType));
	if (entries.length === 0) return undefined;
	const today = sumOf(entries.map(entry => entry.availability?.availableRoomsToday));
	const capacity = sumOf(entries.map(entry => entry.totalRoomCapacity));
	if (today === undefined) return undefined;
	const byWindow: Partial<Record<PickupWindow, number>> = {};
	FIXED_PICKUP_WINDOWS.forEach(window => {
		const ago = sumOf(entries.map(entry => entry.availability?.[AGO_FIELD[window]]));
		byWindow[window] = ago === undefined ? undefined : ago - today;
	});
	return { byWindow, booked: capacity === undefined ? undefined : capacity - today, capacity };
};
